function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export type SignedConversationSession = {
  sessionId: string;
  signedUrl?: string;
  conversationToken?: string;
  raw: unknown;
};

export class ConversationStartCancelledError extends Error {
  constructor() {
    super("Conversation start was cancelled because the widget was disposed");
    this.name = "ConversationStartCancelledError";
  }
}

export class BackendSessionCleanupError extends Error {
  readonly errors: readonly unknown[];

  constructor(startError: unknown, cleanupError: unknown) {
    super("Conversation start and backend cleanup both failed");
    this.name = "BackendSessionCleanupError";
    this.errors = [startError, cleanupError];
  }
}

export function parseSignedConversationSession(value: unknown): SignedConversationSession {
  if (!isRecord(value)) throw new Error("Invalid signed URL response");
  const sessionId = typeof value.session_id === "string" ? value.session_id : undefined;
  const signedUrl = typeof value.url === "string" ? value.url : undefined;
  const conversationToken = typeof value.conversationToken === "string"
    ? value.conversationToken
    : undefined;
  if (!sessionId || (!signedUrl && !conversationToken)) {
    throw new Error("Invalid signed URL response");
  }
  return { sessionId, signedUrl, conversationToken, raw: value };
}

function getIssuedSessionId(value: unknown): string | null {
  if (!isRecord(value)) return null;
  return typeof value.session_id === "string" && value.session_id.length > 0
    ? value.session_id
    : null;
}

type ConversationAttempt = {
  requestSignedSession: () => Promise<unknown>;
  startSdkConversation: (session: SignedConversationSession) => Promise<string>;
  acceptBackendSession: (sessionId: string) => boolean;
  stopSdkConversation: () => Promise<void>;
  endBackendSession: (sessionId: string) => Promise<void>;
};

export async function startIssuedConversation({
  requestSignedSession,
  startSdkConversation,
  acceptBackendSession,
  stopSdkConversation,
  endBackendSession,
}: ConversationAttempt): Promise<{ conversationId: string; session: SignedConversationSession }> {
  const issuedResponse = await requestSignedSession();
  const issuedSessionId = getIssuedSessionId(issuedResponse);
  try {
    const session = parseSignedConversationSession(issuedResponse);
    const conversationId = await startSdkConversation(session);
    if (!acceptBackendSession(session.sessionId)) {
      await stopSdkConversation();
      throw new ConversationStartCancelledError();
    }
    return { conversationId, session };
  } catch (startError) {
    if (!issuedSessionId) throw startError;
    try {
      await endBackendSession(issuedSessionId);
    } catch (cleanupError) {
      throw new BackendSessionCleanupError(startError, cleanupError);
    }
    throw startError;
  }
}
